"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { applyTheme, readStoredTheme } from "@/lib/theme";
import "../pub.css";

export function PubShell({
  children,
  loggedIn,
}: {
  children: ReactNode;
  loggedIn: boolean;
}) {
  const path = usePathname();
  const [open, setOpen] = useState(false);
  const [topOn, setTopOn] = useState(false);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(readStoredTheme() === "dark");
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [path]);

  useEffect(() => {
    const onScroll = () => setTopOn(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dash = loggedIn ? "/dashboard" : "/login?callbackUrl=/dashboard";

  return (
    <div className="pub">
      <header className="pub-nav">
        <div className="pub-nav-inner">
          <Link href="/" className="pub-brand" aria-label="Мира Auto">
            <span className="pub-brand-mark">МИРА</span>
            <span className="pub-brand-sub">auto</span>
          </Link>
          <ul className={`pub-nav-links${open ? " pub-nav-open" : ""}`}>
            <li>
              <Link href="/razbory" aria-current={path.startsWith("/razbory") ? "page" : undefined}>
                Разборы
              </Link>
            </li>
            <li>
              <Link href="/news" aria-current={path.startsWith("/news") ? "page" : undefined}>
                Новости
              </Link>
            </li>
            <li>
              <Link href="/#kak">Как это работает</Link>
            </li>
            <li>
              <Link href="/#sravnenie">Чем не магазин</Link>
            </li>
          </ul>
          <div className="pub-nav-actions">
            <button
              type="button"
              className="dash-theme-btn"
              aria-label={dark ? "Светлая тема" : "Тёмная тема"}
              onClick={() => {
                const next = dark ? "light" : "dark";
                applyTheme(next);
                setDark(next === "dark");
              }}
            >
              {dark ? <SunIcon /> : <MoonIcon />}
            </button>
            <Link href={dash} className="pub-btn pub-btn-primary">
              <span className="pub-nav-cta-full">{loggedIn ? "Дашборд" : "Прогнать VIN"}</span>
              <span className="pub-nav-cta-short">{loggedIn ? "Кабинет" : "VIN"}</span>
            </Link>
            <button
              type="button"
              className="pub-burger"
              aria-expanded={open}
              aria-label="Меню"
              onClick={() => setOpen((v) => !v)}
            >
              <BurgerIcon />
            </button>
          </div>
        </div>
      </header>

      {children}

      <footer className="pub-footer">
        <div className="pub-wrap pub-footer-grid">
          <div>
            <h4>Мира Auto</h4>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              Разборы запчастей: OEM из каталога, цена в городе, отзывы со
              ссылками. Если слоя нет — так и пишем. Своей корзины нет.
            </p>
          </div>
          <div>
            <h4>Разделы</h4>
            <ul>
              <li>
                <Link href="/razbory">Все разборы</Link>
              </li>
              <li>
                <Link href="/razbory/kak-chitat-kartu">Как читать карту</Link>
              </li>
              <li>
                <Link href="/dashboard">Дашборд</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Сервис</h4>
            <ul>
              <li>
                <Link href="/pricing">Тарифы</Link>
              </li>
              <li>
                <Link href="/privacy">Политика</Link>
              </li>
              <li>
                <Link href="/login">Вход</Link>
              </li>
            </ul>
          </div>
        </div>
        <p className="pub-wrap pub-copy">
          © {new Date().getFullYear()} Мира Auto. Не регламент дилера и не
          оферта. Цена — на момент прогона.
        </p>
      </footer>

      <button
        type="button"
        className="pub-top"
        data-on={topOn ? "1" : "0"}
        aria-label="Наверх"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <TopIcon />
      </button>
    </div>
  );
}

function SunIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v1M12 20v1M3 12h1M20 12h1M5.6 5.6l.7.7M17.7 17.7l.7.7M5.6 18.4l.7-.7M17.7 6.3l.7-.7" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446A9 9 0 1 1 12 3z" />
    </svg>
  );
}

function BurgerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function TopIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}
